import cx from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { TrackedLink } from '../link';
import { asset } from '../../services/assetResolverService';

export type TPromoBannerConfig = {
  /** URL the entire banner links to */
  url: string;
  /** Main body content. Pass a string for plain text or JSX for inline styling (e.g. <em>, <strong>, colored spans) */
  blurb: ReactNode;
  /** Button label */
  buttonText: string;
  /** Analytics tracking slug (used as "{slug}:image" and "{slug}:button") */
  trackingSlug: string;
  /** Image path relative to /images/ (served via asset resolver) */
  imagePath: string;
  /** Alt text for the banner image */
  imageAlt: string;
  /** Panel border color */
  borderColor?: string;
  /** Panel background CSS (gradient or solid) */
  background?: string;
  /** Button background color */
  buttonColor?: string;
  /** Button text color. Defaults to white via the .text-white class — override for light button backgrounds. */
  buttonTextColor?: string;
  /** Image display width in pixels (asset requested at 2× for retina). Defaults to 120. */
  imageWidth?: number;
  /** Image display height in pixels (asset requested at 2× for retina). Defaults to 68. */
  imageHeight?: number;
};

type TPromoBannerProps = TPromoBannerConfig & {
  className?: string;
};

const DEFAULT_BORDER_COLOR = '#ce9908';
const DEFAULT_BACKGROUND = 'linear-gradient(76deg, #1a1a2e 0%, #2d1a3e 50%, #1a1a2e 100%)';
const DEFAULT_BUTTON_COLOR = '#9b59b6';
const DEFAULT_IMAGE_WIDTH = 120;
const DEFAULT_IMAGE_HEIGHT = 68;
const ASSET_DENSITY = 2;

export function PromoBanner(props: TPromoBannerProps) {
  const {
    url,
    blurb,
    buttonText,
    trackingSlug,
    imagePath,
    imageAlt,
    borderColor = DEFAULT_BORDER_COLOR,
    background = DEFAULT_BACKGROUND,
    buttonColor = DEFAULT_BUTTON_COLOR,
    buttonTextColor,
    imageWidth = DEFAULT_IMAGE_WIDTH,
    imageHeight = DEFAULT_IMAGE_HEIGHT,
    className
  } = props;

  const panelStyle: CSSProperties = {
    border: `2px solid ${borderColor}`,
    borderRadius: '0.5rem',
    background,
    color: '#fff',
    textDecoration: 'none'
  };

  return (
    <div className={cx(className)}>
      <TrackedLink
        className="slds-p-around--small slds-text-align--center slds-grid slds-grid--vertical-align-center slds-wrap slds-medium-nowrap"
        href={url}
        target="_blank"
        as={trackingSlug}
        style={panelStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <img
            src={asset(imagePath, { width: imageWidth * ASSET_DENSITY, height: imageHeight * ASSET_DENSITY })}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            style={{ borderRadius: '0.25rem' }}
          />
        </div>
        <div className="slds-col rio-m-top-mobi--small slds-p-horizontal--medium slds-text-align--left slds-text-heading--small">
          {blurb}
        </div>
        <div className="slds-col slds-grow slds-p-vertical--small slds-p-right--large slds-align--absolute-center rio-m-top-mobi--small">
          <span
            className={cx(
              'slds-button slds-button--raiderio slds-p-vertical--x-small slds-p-horizontal--small',
              'text-bold slds-text-heading--small text-nowrap',
              { 'text-white rio-text--title': !buttonTextColor }
            )}
            style={{ backgroundColor: buttonColor, color: buttonTextColor }}
          >
            {buttonText}
          </span>
        </div>
      </TrackedLink>
    </div>
  );
}
